/* A5 application only. Content is supplied exclusively by the generated A5 asset. */
(() => {
  'use strict';
  const EDITION_ID = 'cfp-animal-habitats-a5';
  const PROGRESS_KEY = 'cfp-animal-habitats-a5-progress:v1';
  const SCENE_IDS = Object.freeze(['arctic', 'outback', 'coral-reef', 'steppe', 'sonoran', 'primeval', 'cloud-forest', 'madagascar', 'galapagos', 'swampy']);
  const CONTEXT_KEYS = Object.freeze(['sonoran/saguaro-cactus', 'swampy/hibiscus']);
  // Integrity pin, not a second roster: SHA-256 of JSON.stringify(scenes.map(s =>
  // [s.id, s.animals.map(a => a.id)])). Tests derive it independently from the READY map.
  const MEMBERSHIP_SHA256 = '1c4981bcf0a40fb17ff911f0d191e4dd5004cc4c07db9af36515d1b3e4f549a6';
  // Owner-approved ordered [id, printed label, hero filename] presentation, tested independently.
  const PRESENTATION_SHA256 = 'e4e1f57a3c1634119ab504b51fa67ba392d0adaee119eec9f25837fa41e090d3';
  const PAGE_TITLE = 'Animal Habitat Explorer — A5 Mini Edition';
  const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  const require = (condition, message) => { if (!condition) throw new Error(message); };
  const hasText = value => typeof value === 'string' && value.trim().length > 0;
  const freeze = value => {
    if (value && typeof value === 'object') {
      Object.values(value).forEach(freeze);
      Object.freeze(value);
    }
    return value;
  };

  async function sha256(text) {
    require(window.crypto?.subtle, 'Web Crypto is required to verify A5 contracts (HTTPS or localhost)');
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
  }

  async function validatePresentation(input) {
    require(Array.isArray(input), 'Missing A5 presentation map');
    const rows = JSON.parse(JSON.stringify(input));
    require(same(rows.map(row => row?.id), SCENE_IDS), 'A5 presentation must map every scene exactly once, in approved order');
    for (const row of rows) {
      require(hasText(row.label) && /^[a-z0-9-]+\.webp$/.test(row.heroFilename), `${row.id}: missing printed label or hero filename`);
      require(hasText(row.heroImage) && !row.heroImage.includes('{'), `${row.id}: hero file_url was not resolved`);
      const url = new URL(row.heroImage, window.location.href);
      require(['http:', 'https:'].includes(url.protocol) && decodeURIComponent(url.pathname.split('/').pop()) === row.heroFilename, `${row.id}: resolved hero URL does not match its approved filename`);
    }
    const orderedPresentation = JSON.stringify(rows.map(row => [row.id, row.label, row.heroFilename]));
    require(await sha256(orderedPresentation) === PRESENTATION_SHA256, 'A5 printed labels or hero filenames differ from the owner-approved presentation');
    return freeze(Object.fromEntries(rows.map(row => [row.id, row])));
  }

  async function validateEdition(data, metadata) {
    require(Array.isArray(data), 'INITIAL_DATA must be the generated A5 scene array');
    // Snapshot before awaiting the digest: later writes to legacy globals cannot change selection.
    const scenes = JSON.parse(JSON.stringify(data));
    const meta = metadata && JSON.parse(JSON.stringify(metadata));
    require(meta?.editionId === EDITION_ID && meta.schemaVersion === '1.0.0', 'Wrong or missing A5 edition/schema metadata');
    require(meta.counts?.scenes === 10 && meta.counts.animalMemberships === 231 && meta.counts.contextMemberships === 2 && meta.counts.selectableMemberships === 233, 'Wrong A5 semantic counts');
    require(same(meta.contextMemberships, CONTEXT_KEYS), 'Wrong A5 context membership keys');
    require(same(scenes.map(scene => scene?.id), SCENE_IDS), 'Wrong A5 scene IDs/order');
    let entries = 0, contexts = 0;
    for (const scene of scenes) {
      for (const key of ['name', 'location', 'description', 'heroImage', 'color']) require(hasText(scene[key]), `${scene.id}: missing scene ${key}`);
      require(Array.isArray(scene.facts) && scene.facts.length > 0, `${scene.id}: missing habitat facts`);
      for (const fact of scene.facts) for (const key of ['title', 'content', 'icon']) require(hasText(fact?.[key]), `${scene.id}: invalid fact ${key}`);
      require(Array.isArray(scene.animals) && scene.animals.length > 0, `${scene.id}: missing entries`);
      const seen = new Set();
      for (const entry of scene.animals) {
        require(entry && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(entry.id), `${scene.id}: invalid entry ID`);
        require(!seen.has(entry.id), `${scene.id}: duplicate entry ${entry.id}`);
        seen.add(entry.id);
        for (const key of ['name', 'image', 'evolutionaryTrait', 'funFact', 'ecosystemRole', 'diet', 'size', 'sound']) require(hasText(entry[key]), `${scene.id}/${entry.id}: missing ${key}`);
        entries++;
        if (CONTEXT_KEYS.includes(`${scene.id}/${entry.id}`)) contexts++;
      }
    }
    require(entries === 233 && contexts === 2 && entries - contexts === 231, 'Actual A5 membership counts differ from metadata');
    const orderedIds = JSON.stringify(scenes.map(scene => [scene.id, scene.animals.map(entry => entry.id)]));
    require(await sha256(orderedIds) === MEMBERSHIP_SHA256, 'A5 entry IDs/order differ from the approved membership contract');
    return freeze({ scenes, metadata: meta });
  }

  function refreshIcons() {
    try { window.lucide?.createIcons(); }
    catch (error) { console.warn('[A5] Icons could not be refreshed', error); }
  }

  function createApp() {
    // Timers and browser listeners belong to this component, outside Alpine's reactive data.
    let closeTimer = null, closeGeneration = 0, popListener = null, hashListener = null;
    let historyPosition = 0, destroyed = false, completionTimer = null;
    let navigationGeneration = 0, previousScrollRestoration = null, hudObserver = null;
    let homeScroll = { left: 0, top: 0, mainLeft: 0, mainTop: 0 };
    let detailOpener = null;
    return {
      ready: false, failed: false, canPersist: true,
      biomes: [], editionMeta: null, presentation: {}, collectedStickers: [],
      selectedBiomeId: null, selectedAnimal: null,
      isModalOpen: false, isJumpMenuOpen: false, isPassportOpen: false, scrollY: 0,
      completionCelebratedThisSession: false, completionAnnouncement: '',
      get activeBiome() { return this.biomes.find(scene => scene.id === this.selectedBiomeId) || null; },
      get progressPercent() { return this.biomes.length ? Math.min(100, this.collectedStickers.length / this.biomes.length * 100) : 0; },
      get remainingHabitats() { return this.biomes.length - this.collectedStickers.length; },
      get isComplete() { return this.ready && this.biomes.length > 0 && this.remainingHabitats === 0; },
      get firstUnexplored() { return this.biomes.find(scene => !this.collectedStickers.includes(scene.id)) || null; },
      sceneLabel(id) { return this.biomes.some(scene => scene.id === id) ? this.presentation[id]?.label || '' : ''; },
      sceneHero(id) { return this.biomes.some(scene => scene.id === id) ? this.presentation[id]?.heroImage || '' : ''; },
      isContext(sceneId, entryId) { return !!this.editionMeta?.contextMemberships.includes(`${sceneId}/${entryId}`); },
      sceneCounts(scene) {
        const contexts = scene.animals.filter(entry => this.isContext(scene.id, entry.id)).length;
        return { animals: scene.animals.length - contexts, contexts, selectable: scene.animals.length };
      },
      async init() {
        document.documentElement.dataset.a5Initializing = 'true';
        try {
          require(document.documentElement.dataset.a5Boot !== 'failed', 'A required A5 asset failed to load');
          const presentationNode = document.getElementById('cfp-a5-presentation');
          require(presentationNode, 'Missing A5 presentation configuration');
          const presentationInput = JSON.parse(presentationNode.textContent);
          const validated = await validateEdition(window.INITIAL_DATA, window.CFP_A5_EDITION_META);
          const presentation = await validatePresentation(presentationInput);
          if (destroyed) return;
          this.biomes = validated.scenes;
          this.editionMeta = validated.metadata;
          this.presentation = presentation;
          this.readProgress();
          this.ready = true;
          const hud = document.querySelector('.a5-scene-hud');
          if (hud && window.ResizeObserver) {
            hudObserver = new window.ResizeObserver(() => this.syncHudHeight());
            hudObserver.observe(hud);
          }
          this.setupHistory();
          document.documentElement.dataset.a5Boot = 'ready';
          this.afterRender();
        } catch (error) {
          this.biomes = []; this.editionMeta = null; this.presentation = {}; this.collectedStickers = [];
          this.selectedBiomeId = null; this.selectedAnimal = null; this.isModalOpen = false;
          this.ready = false; this.failed = true;
          document.documentElement.dataset.a5Boot = 'failed';
          console.error('[A5] Explorer unavailable:', error);
        }
      },
      readProgress() {
        try {
          const raw = window.localStorage.getItem(PROGRESS_KEY);
          if (raw === null) return;
          const value = JSON.parse(raw);
          if (value?.version !== 1 || value.editionId !== EDITION_ID || !Array.isArray(value.exploredSceneIds)) return;
          this.collectedStickers = [...new Set(value.exploredSceneIds.filter(id => typeof id === 'string' && this.biomes.some(scene => scene.id === id)))];
        } catch (error) {
          this.collectedStickers = [];
          // A malformed value may be replaced on the next successful save.
          if (!(error instanceof SyntaxError)) this.canPersist = false;
        }
      },
      toggleSticker(id) {
        if (!this.ready || !this.biomes.some(scene => scene.id === id)) return false;
        const previousCount = this.collectedStickers.length;
        const removing = this.collectedStickers.includes(id);
        this.collectedStickers = removing ? this.collectedStickers.filter(value => value !== id) : [...this.collectedStickers, id];
        try {
          window.localStorage.setItem(PROGRESS_KEY, JSON.stringify({ version: 1, editionId: EDITION_ID, exploredSceneIds: this.collectedStickers }));
          this.canPersist = true;
        } catch { this.canPersist = false; }
        if (removing) {
          this.cancelCompletionOpening();
          this.completionAnnouncement = '';
        } else if (previousCount < this.biomes.length && this.isComplete) {
          if (!this.completionCelebratedThisSession) {
            this.completionCelebratedThisSession = true;
            this.completionAnnouncement = `You explored all ${this.biomes.length} habitats! Your Habitat Passport is complete.`;
            this.celebrateProgress(true);
            // Let the mark visibly settle. Never interrupt a later navigation or another dialog.
            const completedScene = this.selectedBiomeId;
            completionTimer = setTimeout(() => {
              completionTimer = null;
              if (!destroyed && this.isComplete && this.selectedBiomeId === completedScene && !this.isModalOpen && !this.isJumpMenuOpen && !this.isPassportOpen) this.openPassport();
            }, 500);
          }
        } else {
          this.celebrateProgress(false);
        }
        return true;
      },
      celebrateProgress(completed) {
        try {
          // If motion preference cannot be read, the static stamp still supplies the reward.
          if (!window.matchMedia || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
          window.confetti?.({ particleCount: completed ? 55 : 32, spread: 60, ticks: 75, gravity: 1.2,
            origin: { y: 0.6 }, zIndex: 80, disableForReducedMotion: true,
            colors: ['#A7C957', '#F59E0B', '#FDFBF7', '#386641', '#E63946'] });
        } catch { /* Optional decoration must not interrupt progress. */ }
      },
      cancelCompletionOpening() {
        clearTimeout(completionTimer); completionTimer = null;
      },
      openPassport() {
        if (!this.ready || destroyed || this.isModalOpen || this.isJumpMenuOpen || this.isPassportOpen) return false;
        this.cancelCompletionOpening();
        this.isPassportOpen = true;
        this.writeRoute('push', true);
        this.focusPassport();
        return true;
      },
      focusPassport() {
        this.$nextTick(() => {
          if (!destroyed && this.isPassportOpen) document.getElementById('a5-passport-close')?.focus({ preventScroll: true });
        });
      },
      focusProgress() {
        this.$nextTick(() => {
          if (!destroyed && !this.isPassportOpen && !this.isModalOpen && !this.isJumpMenuOpen) {
            document.getElementById(this.selectedBiomeId ? 'a5-scene-progress-button' : 'a5-progress-button')?.focus({ preventScroll: true });
          }
        });
      },
      closePassport() {
        if (!this.isPassportOpen) return false;
        this.isPassportOpen = false;
        // Match detail Close: synchronous replacement avoids a queued Back closing a rapid reopen.
        this.writeRoute('replace', false, true);
        this.focusProgress();
        return true;
      },
      visitPassportHabitat(id) {
        if (!this.isPassportOpen || !this.biomes.some(scene => scene.id === id)) return false;
        if (id === this.selectedBiomeId) return this.closePassport();
        // Consume the overlay entry: Home -> Passport -> habitat becomes Home -> habitat.
        return this.selectBiome(id, 'replace');
      },
      continueExploring() {
        return this.firstUnexplored ? this.visitPassportHabitat(this.firstUnexplored.id) : false;
      },
      openJumpMenu() {
        if (!this.ready || this.isModalOpen || this.isPassportOpen) return false;
        this.cancelCompletionOpening();
        this.isJumpMenuOpen = true;
        return true;
      },
      afterRender(positionView = false) {
        const destination = this.selectedBiomeId;
        const generation = navigationGeneration;
        this.$nextTick(() => {
          refreshIcons();
          if (!positionView) return;
          // Alpine has replaced the route DOM; the next frame supplies its final layout.
          requestAnimationFrame(() => {
            if (destroyed || generation !== navigationGeneration || destination !== this.selectedBiomeId) return;
            this.syncHudHeight();
            if (destination) {
              // Position every scrolling ancestor, including the document. The padded
              // semantic start reserves the actual HUD height above the hero.
              document.querySelector('[data-a5-habitat-start]')?.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'instant' });
            } else {
              const main = document.querySelector('main');
              if (main) { main.scrollLeft = homeScroll.mainLeft; main.scrollTop = homeScroll.mainTop; }
              window.scrollTo({ left: homeScroll.left, top: homeScroll.top, behavior: 'instant' });
            }
            const main = document.querySelector('main');
            this.scrollY = (window.scrollY || 0) + (main?.scrollTop || 0);
            // An overlay opened during this navigation keeps focus, while its new
            // underlying route still gets the same deterministic starting position.
            if (!this.isPassportOpen && !this.isModalOpen && !this.isJumpMenuOpen) {
              document.getElementById(destination ? 'a5-scene-title' : 'a5-home-title')?.focus({ preventScroll: true });
            }
          });
        });
      },
      syncHudHeight() {
        const height = document.querySelector('.a5-scene-hud')?.getBoundingClientRect().height;
        if (height > 0) document.body.style.setProperty('--a5-hud-height', `${height}px`);
      },
      selectBiome(id, mode = 'push') {
        if (!this.ready) return false;
        const allowed = id === null || this.biomes.some(scene => scene.id === id);
        this.applyRoute(allowed ? id : null, null);
        this.writeRoute(mode === 'replace' ? 'replace' : 'push');
        return allowed;
      },
      openAnimalModal(entryId) {
        if (!this.ready || this.isPassportOpen) return false;
        const entry = this.activeBiome?.animals.find(record => record.id === entryId);
        if (!entry) return false;
        this.showEntry(entry);
        this.writeRoute('push');
        return true;
      },
      showEntry(entry) {
        const canonical = this.activeBiome?.animals.find(record => record.id === entry?.id);
        if (!canonical || this.isPassportOpen) return;
        // Capture before Alpine makes main inert; the browser can then move activeElement
        // to body before the focus plugin records its own return target.
        if (!this.isModalOpen) detailOpener = document.activeElement;
        this.cancelCompletionOpening();
        clearTimeout(closeTimer); closeTimer = null; closeGeneration++;
        this.isJumpMenuOpen = false;
        this.selectedAnimal = canonical;
        this.isModalOpen = true;
        this.afterRender();
      },
      closeAnimalModal() {
        if (!this.isModalOpen) return;
        this.hideEntry();
        // Replace rather than enqueue an asynchronous history.back() which could close a rapid reopen.
        this.writeRoute('replace');
      },
      hideEntry() {
        if (!this.isModalOpen && !this.selectedAnimal) return;
        this.isModalOpen = false;
        clearTimeout(closeTimer);
        const generation = ++closeGeneration;
        const scene = this.selectedBiomeId;
        const opener = detailOpener;
        this.$nextTick(() => {
          if (destroyed || generation !== closeGeneration || scene !== this.selectedBiomeId || this.isModalOpen || this.isPassportOpen || this.isJumpMenuOpen) return;
          const target = opener?.isConnected && opener !== document.body ? opener : document.getElementById('a5-scene-title');
          target?.focus({ preventScroll: true });
        });
        closeTimer = setTimeout(() => {
          if (generation === closeGeneration && !this.isModalOpen) this.selectedAnimal = null;
        }, 300);
      },
      applyRoute(sceneId, entryId) {
        const previousScene = this.selectedBiomeId;
        const destination = this.biomes.some(scene => scene.id === sceneId) ? sceneId : null;
        if (previousScene !== destination) {
          navigationGeneration++;
          if (previousScene === null) {
            const main = document.querySelector('main');
            homeScroll = { left: window.scrollX || 0, top: window.scrollY || 0,
              mainLeft: main?.scrollLeft || 0, mainTop: main?.scrollTop || 0 };
          }
        }
        this.cancelCompletionOpening();
        this.isPassportOpen = false;
        this.isJumpMenuOpen = false;
        this.selectedBiomeId = destination;
        if (previousScene !== this.selectedBiomeId) {
          clearTimeout(closeTimer); closeTimer = null; closeGeneration++;
          this.selectedAnimal = null; this.isModalOpen = false;
        }
        const entry = this.activeBiome?.animals.find(record => record.id === entryId);
        if (entry) this.showEntry(entry);
        else this.hideEntry();
        if (previousScene !== destination) this.afterRender(true);
      },
      routeHash() {
        if (!this.selectedBiomeId) return '';
        const params = new URLSearchParams({ habitat: this.selectedBiomeId });
        if (this.isModalOpen && this.selectedAnimal) params.set('entry', this.selectedAnimal.id);
        return `#${params}`;
      },
      readRoute() {
        const params = new URLSearchParams(window.location.hash.slice(1));
        const keys = [...params.keys()];
        const sceneId = params.get('habitat');
        const entryId = params.get('entry');
        if (new Set(keys).size !== keys.length || keys.some(key => !['habitat', 'entry'].includes(key))) return { sceneId: null, entryId: null };
        const scene = this.biomes.find(candidate => candidate.id === sceneId);
        if (!scene) return { sceneId: null, entryId: null };
        return { sceneId: scene.id, entryId: scene.animals.some(entry => entry.id === entryId) ? entryId : null };
      },
      writeRoute(mode, passport = false, closePassport = false) {
        const printedLabel = this.sceneLabel(this.selectedBiomeId);
        document.title = [this.isModalOpen ? this.selectedAnimal?.name : null, printedLabel, PAGE_TITLE].filter(Boolean).join(' | ');
        try {
          const hash = this.routeHash();
          const current = window.history.state?.cfpA5;
          const ownedCurrent = current?.editionId === EDITION_ID && current.position === historyPosition;
          // Reuse the just-closed overlay slot. Repeated open/close must not grow identical
          // Home/scene entries, and the next real navigation can consume that slot too.
          if (mode === 'push' && ownedCurrent && current.passportClosed === true) mode = 'replace';
          if (mode === 'push' && !passport && hash === window.location.hash) return;
          const position = mode === 'push' ? historyPosition + 1 : historyPosition;
          const overlay = passport ? { passport: true } : closePassport && ownedCurrent && current.passport === true ? { passportClosed: true } : {};
          window.history[mode === 'push' ? 'pushState' : 'replaceState']({ cfpA5: { editionId: EDITION_ID, position, ...overlay } }, document.title, `${window.location.pathname}${window.location.search}${hash}`);
          historyPosition = position;
        } catch (error) { console.warn('[A5] Browser history unavailable; internal navigation remains usable', error); }
      },
      setupHistory() {
        // This standalone app owns view positioning. Native persisted scroll restoration
        // after popstate must not overwrite a newly positioned habitat or move an overlay.
        try {
          previousScrollRestoration = window.history.scrollRestoration;
          window.history.scrollRestoration = 'manual';
        } catch { /* Internal navigation also works when History APIs are unavailable. */ }
        const route = this.readRoute();
        this.applyRoute(route.sceneId, route.entryId);
        const existing = window.history.state?.cfpA5;
        const owned = existing?.editionId === EDITION_ID && Number.isInteger(existing.position) && existing.position >= 0;
        if (owned) {
          // Reloads preserve the existing app stack instead of appending more scene/entry states.
          historyPosition = existing.position;
          this.writeRoute('replace');
        } else {
        // A direct entry link gets its own Home -> Habitat -> Entry stack, all inside this app.
        const destinationScene = this.selectedBiomeId;
        const destinationEntry = this.isModalOpen ? this.selectedAnimal.id : null;
        this.selectedBiomeId = null; this.isModalOpen = false;
        this.writeRoute('replace');
        if (destinationScene) {
          this.selectedBiomeId = destinationScene; this.writeRoute('push');
          if (destinationEntry) { this.isModalOpen = true; this.writeRoute('push'); }
        }
        }
        popListener = event => {
          const before = this.routeHash();
          const wasPassportOpen = this.isPassportOpen;
          const previousPosition = historyPosition;
          const state = event.state?.cfpA5;
          const owned = state?.editionId === EDITION_ID && Number.isInteger(state.position) && state.position >= 0;
          historyPosition = owned ? state.position : 0;
          const next = this.readRoute();
          this.applyRoute(next.sceneId, next.entryId);
          this.isPassportOpen = owned && state.passport === true && !this.isModalOpen;
          if (this.isPassportOpen) this.focusPassport();
          else if (wasPassportOpen && before === this.routeHash()) this.focusProgress();
          // Internal Close replaces the entry with its scene. Skip only an owned duplicate
          // when travelling backward, so one subsequent Back still returns toward Home.
          if (!wasPassportOpen && !this.isPassportOpen && owned && historyPosition > 0 && historyPosition < previousPosition && before === this.routeHash()) window.history.back();
          else this.writeRoute('replace', this.isPassportOpen);
        };
        hashListener = () => {
          // A popstate may be followed by hashchange for that same traversal. Preserve only
          // its owned Passport marker; a manually changed hash receives no marker.
          const state = window.history.state?.cfpA5;
          const passport = state?.editionId === EDITION_ID && Number.isInteger(state.position) && state.position >= 0 && state.passport === true;
          const next = this.readRoute();
          this.applyRoute(next.sceneId, next.entryId);
          this.isPassportOpen = passport && !this.isModalOpen;
          this.writeRoute('replace', this.isPassportOpen);
          if (this.isPassportOpen) this.focusPassport();
        };
        window.addEventListener('popstate', popListener);
        window.addEventListener('hashchange', hashListener);
      },
      destroy() {
        destroyed = true; clearTimeout(closeTimer); closeGeneration++; this.cancelCompletionOpening();
        navigationGeneration++;
        hudObserver?.disconnect();
        try {
          if (previousScrollRestoration != null) window.history.scrollRestoration = previousScrollRestoration;
        } catch { /* A restricted History API must not prevent cleanup. */ }
        if (popListener) window.removeEventListener('popstate', popListener);
        if (hashListener) window.removeEventListener('hashchange', hashListener);
      }
    };
  }
  window.CFP_A5_APP = Object.freeze({ createApp, validateEdition, validatePresentation, refreshIcons, PROGRESS_KEY, SCENE_IDS, MEMBERSHIP_SHA256, PRESENTATION_SHA256 });
  document.addEventListener('alpine:init', () => window.Alpine.data('cfpAnimalHabitatsA5App', createApp));
})();
